import { useState, useEffect } from "react";
import { useAdvanceWizardStep } from "../../hooks/use-wizard.js";
import { Button } from "../../components/button.js";
import type { WizardState, ScanDepth } from "../../api/types.js";

interface ScanDepthOption {
  value: ScanDepth;
  label: string;
  description: string;
  recommended?: boolean;
  maxPages: number;
}

const ALL_OPTIONS: ScanDepthOption[] = [
  {
    value: "10",
    label: "10 pages",
    description: "Great for small or brochure sites.",
    maxPages: 10,
  },
  {
    value: "20",
    label: "20 pages",
    description: "Good for growing sites with a few sections.",
    maxPages: 20,
  },
  {
    value: "50",
    label: "50 pages",
    description: "Good coverage for medium-sized sites.",
    maxPages: 50,
  },
  {
    value: "100",
    label: "100 pages",
    description: "Comprehensive scanning for most sites.",
    maxPages: 100,
  },
  {
    value: "150",
    label: "150 pages",
    description: "Full coverage for larger business sites.",
    maxPages: 150,
  },
  {
    value: "250",
    label: "250 pages",
    description: "Deep coverage for complex multi-section sites.",
    maxPages: 250,
  },
  {
    value: "500",
    label: "500 pages",
    description: "Extensive scanning for large content-heavy sites.",
    maxPages: 500,
  },
  {
    value: "1000",
    label: "1,000 pages",
    description: "Maximum depth for enterprise-scale websites.",
    maxPages: 1000,
  },
];

/** Max pages per scan for each plan (sourced from plan-seed.ts) */
const PLAN_MAX_PAGES: Record<string, number> = {
  free: 10,
  starter: 150,
  growth: 250,
  pro: 1000,
  enterprise: 99999,
};

interface ScanDepthStepProps {
  websiteId: string;
  wizardState: WizardState;
}

export function ScanDepthStep({ websiteId, wizardState }: ScanDepthStepProps) {
  const existingData = wizardState.steps.find(
    (s) => s.step === "scan_depth",
  )?.data;

  // Determine plan limit from selected plan
  const planData = wizardState.steps.find(
    (s) => s.step === "plan_selection",
  )?.data;
  const planId = (planData?.planId as string | undefined) ?? "free";
  const planMaxPages = PLAN_MAX_PAGES[planId] ?? 10;

  // Filter options to those within the plan's page limit
  const options = ALL_OPTIONS.filter((opt) => opt.maxPages <= planMaxPages);

  // Mark the highest available option as recommended (best coverage within limit)
  const recommendedValue = options.length > 0 ? options[options.length - 1]!.value : "10";

  const existingDepth = (existingData?.depth as ScanDepth | undefined) ?? recommendedValue;
  // If previously selected depth exceeds plan limit, reset to recommended
  const validDepth = options.some((o) => o.value === existingDepth)
    ? existingDepth
    : recommendedValue;

  const [selected, setSelected] = useState<ScanDepth>(validDepth);
  const advance = useAdvanceWizardStep(websiteId);

  // Auto-advance when only one option is available (e.g. Free plan = 10 pages only)
  const [autoAdvanced, setAutoAdvanced] = useState(false);
  useEffect(() => {
    if (options.length === 1 && !autoAdvanced && !advance.isPending) {
      setAutoAdvanced(true);
      advance.mutate({
        step: "scan_depth",
        outcome: "pass",
        data: { depth: options[0]!.value },
      });
    }
  }, [options.length, autoAdvanced, advance]);

  // If auto-advancing, show brief feedback
  if (options.length === 1 && autoAdvanced) {
    return (
      <div className="wizard-content">
        <h2 className="wizard-content__title">Scan depth</h2>
        <p className="text-muted">
          Your plan scans up to {planMaxPages.toLocaleString()} pages. Continuing…
        </p>
      </div>
    );
  }

  function handleContinue() {
    advance.mutate({
      step: "scan_depth",
      outcome: "pass",
      data: { depth: selected },
    });
  }

  return (
    <div className="wizard-content">
      <h2 className="wizard-content__title">Scan depth</h2>
      <p className="wizard-content__subtitle">
        Choose the maximum pages to scan per audit. The scanner selects
        representative pages from each template group, up to this limit.
      </p>

      <div className="option-grid" role="radiogroup" aria-label="Scan depth options">
        {options.map((opt) => (
          <div
            key={opt.value}
            className={`option-card${selected === opt.value ? " option-card--selected" : ""}${opt.value === recommendedValue ? " option-card--recommended" : ""}`}
            onClick={() => setSelected(opt.value)}
            role="radio"
            aria-checked={selected === opt.value}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setSelected(opt.value);
            }}
          >
            <p className="option-card__title">{opt.label}</p>
            <p className="option-card__desc">{opt.description}</p>
          </div>
        ))}
      </div>

      <p className="text-sm text-muted">
        Your plan allows up to {planMaxPages.toLocaleString()} pages per scan.
        You can upgrade your plan later to unlock deeper scanning.
      </p>

      {advance.error && (
        <div className="alert alert--error mt-3">{advance.error.message}</div>
      )}

      <div className="wizard-content__actions">
        <Button onClick={handleContinue} loading={advance.isPending}>
          Continue
        </Button>
      </div>
    </div>
  );
}
